import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  FaBalanceScale,
  FaCheckCircle,
  FaChevronDown,
  FaChevronUp,
  FaClock,
  FaDollarSign,
  FaClipboardList,
  FaEye,
  FaFileAlt,
  FaIndustry,
  FaRegClock,
  FaShieldAlt,
  FaSignature,
  FaUserFriends,
  FaStar,
  FaTimesCircle,
  FaTimes,
  FaTruck,
} from "react-icons/fa";
import { toast } from "react-toastify";
import "bootstrap/dist/css/bootstrap.min.css";
import "../../assets/styles/buyer_contract_manufacturer_quotes.scss";
import CommonService from "../../services/common.service";
import { API_URLS } from "../../constants/api-urls";
import { useProductDetailsMaster } from "../../hooks/useProductDetailsMaster";
import { useFactoryDetailsMaster } from "../../hooks/useFactoryDetailsMaster";
import BuyerCmQcRequirementsModal from "../../components/BuyerCmQcRequirementsModal";
import LinkedLogisticsPanel from "../../components/moduleLogistics/LinkedLogisticsPanel";

/** Compact circular icon control with tooltip + screen-reader label */
function CmQuoteIconBtn({ title, onClick, disabled, primary, withLabel, children }) {
  return (
    <button
      type="button"
      className={[
        "cm-quote-icon-btn",
        primary ? "cm-quote-icon-btn--primary" : "",
        withLabel ? "cm-quote-icon-btn--with-label" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      title={title}
      aria-label={title}
      onClick={onClick}
      disabled={disabled}
    >
      {children}
    </button>
  );
}

/** Navigate target for buyer CM “recruit / change external QA” (must stay under dashboard, not public /contract-manufacturers/rfq). */
function buildRecruitExternalQaLocation(cmRfqId, companyIdFilter, quoteTableId) {
  const id = Number(cmRfqId);
  if (!Number.isFinite(id) || id <= 0) return null;
  const qs = new URLSearchParams();
  if (Number.isFinite(companyIdFilter) && companyIdFilter > 0) qs.set("companyId", String(companyIdFilter));
  if (quoteTableId != null && Number.isFinite(Number(quoteTableId)) && Number(quoteTableId) > 0) {
    qs.set("quoteId", String(quoteTableId));
  }
  const search = qs.toString();
  return {
    pathname: `/dashboard/buyer/contract-manufacturers/recruit-external-qa/${id}`,
    search: search ? `?${search}` : "",
  };
}

function toNumber(v) {
  if (v == null || v === "") return NaN;
  const n = Number(String(v).replace(/,/g, ""));
  return Number.isFinite(n) ? n : NaN;
}

function formatCurrency(amount, currency = "USD") {
  const n = toNumber(amount);
  if (!Number.isFinite(n)) return "—";
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: currency || "USD",
      maximumFractionDigits: 0,
    }).format(n);
  } catch {
    return `${currency || "USD"} ${n}`;
  }
}

function formatDate(v) {
  if (!v) return "—";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

function deriveValidUntil(quotedAt, priceValidityDays) {
  const base = quotedAt ? new Date(quotedAt) : null;
  if (!base || Number.isNaN(base.getTime())) return "—";
  const days = toNumber(priceValidityDays);
  if (!Number.isFinite(days)) return "—";
  base.setDate(base.getDate() + days);
  return formatDate(base);
}

function getObj(v) {
  return v && typeof v === "object" && !Array.isArray(v) ? v : {};
}

/** P2P dispatch stores the buyer internal CM RFQ id on manufacturer copies. */
function resolveInternalCmRfqIdFromPayload(payload) {
  const p = getObj(payload);
  const meta = getObj(p._meta);
  const candidates = [p.p2pSourceCmRfqId, meta.p2pSourceCmRfqId];
  for (const src of candidates) {
    const n = Number(src);
    if (Number.isFinite(n) && n > 0) return n;
  }
  return null;
}

/** Quotes are only comparable within the same buyer RFQ thread (P2P internal, broadcast batch, or direct send). */
function resolveCmQuoteGroupKey(row) {
  const payload = getObj(row?.payload_json);
  const internalId = resolveInternalCmRfqIdFromPayload(payload);
  if (internalId != null) return `p2p:${internalId}`;
  const batchId = row?.broadcast_batch_id != null ? String(row.broadcast_batch_id).trim() : "";
  if (batchId) return `broadcast:${batchId}`;
  if (String(row?.flow_type || "").trim() === "p2p_internal") return `p2p:${row.id}`;
  return `direct:${row.id}`;
}

function rowBelongsToInternalRfq(row, internalRfqId) {
  const target = Number(internalRfqId);
  if (!Number.isFinite(target) || target <= 0) return true;
  if (Number(row?.id) === target) return true;
  return resolveInternalCmRfqIdFromPayload(row?.payload_json) === target;
}

function pickLatestQuoteGroupKey(rows) {
  const byGroup = new Map();
  rows.forEach((row) => {
    const key = resolveCmQuoteGroupKey(row);
    const ts = new Date(
      row?.quote_updated_at ||
        row?.quote_submitted_at ||
        row?.quoteSubmittedAt ||
        row?.updated_at ||
        row?.created_at ||
        0,
    ).getTime();
    const prev = byGroup.get(key);
    if (!prev || ts > prev.ts) byGroup.set(key, { ts, key });
  });
  if (byGroup.size === 0) return null;
  return [...byGroup.values()].sort((a, b) => b.ts - a.ts)[0].key;
}

function scopeRowsToQuoteContext(rows, rfqIdFilter) {
  if (!rows.length) return rows;
  if (Number.isFinite(rfqIdFilter) && rfqIdFilter > 0) {
    return rows.filter((row) => {
      if (!rowBelongsToInternalRfq(row, rfqIdFilter)) return false;
      const hasQuote = Boolean(
        row?.quoteSubmitted ||
          row?.quote_submitted ||
          row?.quote_table_id ||
          row?.quote_status,
      );
      if (String(row?.flow_type || "").trim() === "p2p_internal" && !hasQuote) return false;
      return hasQuote;
    });
  }
  const latestKey = pickLatestQuoteGroupKey(rows);
  if (!latestKey || rows.every((row) => resolveCmQuoteGroupKey(row) === latestKey)) return rows;
  return rows.filter((row) => resolveCmQuoteGroupKey(row) === latestKey);
}

function extractApiErrorMessage(err) {
  return err?.response?.data?.message || err?.message || "Request failed";
}

function resolveUnitLabel(rawUnit, ...optionSets) {
  const raw = String(rawUnit || "").trim();
  if (!raw) return "";
  const allOptions = optionSets.flat().filter(Boolean);
  const byValue = allOptions.find((opt) => String(opt?.value) === raw);
  if (byValue?.label) return String(byValue.label).trim();
  const byLabel = allOptions.find((opt) => String(opt?.label || "").toLowerCase() === raw.toLowerCase());
  if (byLabel?.label) return String(byLabel.label).trim();
  return raw;
}

function resolveOptionLabel(rawValue, options = []) {
  const raw = String(rawValue ?? "").trim();
  if (!raw) return "";
  const byValue = options.find((opt) => String(opt?.value ?? "").trim() === raw);
  if (byValue?.label) return String(byValue.label).trim();
  const byLabel = options.find((opt) => String(opt?.label ?? "").trim().toLowerCase() === raw.toLowerCase());
  if (byLabel?.label) return String(byLabel.label).trim();
  return raw;
}

function computeQuoteTotals(basePrice, fallbackAmount) {
  const base = getObj(basePrice);
  const basePriceValue = toNumber(base?.price);
  const taxParts = [
    toNumber(base?.cgst),
    toNumber(base?.sgst),
    toNumber(base?.igst),
    toNumber(base?.cess),
    toNumber(base?.vat),
    toNumber(base?.otherTaxes),
  ].filter((n) => Number.isFinite(n));
  const totalTaxPercent = taxParts.reduce((a, b) => a + b, 0);
  if (!Number.isFinite(basePriceValue)) {
    const fallback = toNumber(fallbackAmount);
    return {
      baseAmount: Number.isFinite(fallback) ? fallback : NaN,
      taxPercent: totalTaxPercent,
      taxAmount: Number.isFinite(fallback) ? (fallback * totalTaxPercent) / 100 : NaN,
      totalAmount: Number.isFinite(fallback) ? fallback + (fallback * totalTaxPercent) / 100 : NaN,
    };
  }
  const taxAmount = (basePriceValue * totalTaxPercent) / 100;
  return {
    baseAmount: basePriceValue,
    taxPercent: totalTaxPercent,
    taxAmount,
    totalAmount: basePriceValue + taxAmount,
  };
}

function deriveQuantity(rfqPayload, quotePayload, dimensionUnitOptions = [], attributeUnitOptions = []) {
  const project = getObj(rfqPayload?.project);
  const commercialFromRfq = getObj(rfqPayload?.commercial);
  const commercialFromQuote = getObj(quotePayload?.commercial);
  const base = getObj(quotePayload?.basePrice);
  const products = Array.isArray(rfqPayload?.products) ? rfqPayload.products : [];
  const commercialUnitRaw =
    commercialFromRfq?.unitOfMeasurement ||
    commercialFromQuote?.unitOfMeasurement ||
    commercialFromRfq?.unit_of_measurement ||
    commercialFromQuote?.unit_of_measurement ||
    "";
  const commercialUnit = resolveUnitLabel(commercialUnitRaw, attributeUnitOptions, dimensionUnitOptions);

  const projectCandidates = [
    project?.quantity,
    project?.estimatedOrderVolume,
    project?.estimatedAnnualUsage,
    project?.lifetimeVolume,
  ];
  for (const v of projectCandidates) {
    if (v != null && String(v).trim()) {
      return commercialUnit ? `${String(v).trim()} ${commercialUnit}` : String(v).trim();
    }
  }

  const qtyNums = products
    .map((p) => toNumber(getObj(p)?.quantity))
    .filter((n) => Number.isFinite(n));
  if (qtyNums.length > 0) {
    const total = qtyNums.reduce((a, b) => a + b, 0);
    const rawUom = products
      .map((p) => String(getObj(p)?.uom || "").trim())
      .find(Boolean);
    const firstUom = commercialUnit || resolveUnitLabel(rawUom, attributeUnitOptions, dimensionUnitOptions);
    return firstUom ? `${total.toLocaleString()} ${firstUom}` : total.toLocaleString();
  }

  const baseQty = base?.quantity ?? base?.moq;
  if (baseQty != null && String(baseQty).trim()) return String(baseQty).trim();
  return "—";
}

export default function BuyerContractManufacturerQuotesPage() {
  const { dimensionUnitOptions, attributeUnitOptions } = useProductDetailsMaster();
  const { paymentMethodOptions, paymentScheduleOptions, shippingTermsOptions, modeOfTransportOptions } = useFactoryDetailsMaster();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const companyIdFilter = Number(searchParams.get("companyId"));
  const rfqIdFilter = Number(searchParams.get("rfqId"));
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [manufacturerMetaById, setManufacturerMetaById] = useState(new Map());
  const [statusTab, setStatusTab] = useState("all");
  const [error, setError] = useState("");
  const [detailQuote, setDetailQuote] = useState(null);
  const [acceptQuote, setAcceptQuote] = useState(null);
  const [rejectQuote, setRejectQuote] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [rejectErrors, setRejectErrors] = useState({});
  const [rejecting, setRejecting] = useState(false);
  const [qcModalOpen, setQcModalOpen] = useState(false);
  const [qcModalProductCode, setQcModalProductCode] = useState("");
  const [qcModalRfqId, setQcModalRfqId] = useState(null);
  const [qcModalReadOnly, setQcModalReadOnly] = useState(false);
  const [qcModalBuyerReviewPending, setQcModalBuyerReviewPending] = useState(false);
  const [qcModalManufacturerName, setQcModalManufacturerName] = useState("");
  const [qcInitialData, setQcInitialData] = useState(null);
  const [externalQaPromptForRfq, setExternalQaPromptForRfq] = useState(null);
  const [externalQaSavingDecision, setExternalQaSavingDecision] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    commercial: false,
    delivery: false,
    quality: false,
    warranty: false,
    pricing: false,
    manufacturer: false,
  });

  const looksLikeLocationId = (v) => {
    if (v == null || v === "") return false;
    return /^\d+$/.test(String(v).trim());
  };

  const getLocationFromManufacturer = (item) => {
    const fd = item?.facility_details || {};
    const cityLabel = fd.cityName || fd.city_name || fd.cityLabel || fd.displayCity;
    const stateLabel = fd.stateName || fd.state_name || fd.stateLabel || fd.displayState;
    if (cityLabel || stateLabel) return [cityLabel, stateLabel].filter(Boolean).join(", ");
    const cityRaw = fd.city ?? fd.address?.city;
    const stateRaw = fd.state ?? fd.address?.state;
    if (!looksLikeLocationId(cityRaw) && !looksLikeLocationId(stateRaw)) {
      if (cityRaw || stateRaw) return [cityRaw, stateRaw].filter(Boolean).join(", ");
    }
    const addr = item?.address || fd.address;
    if (typeof addr === "string" && addr.trim()) return addr.trim();
    if (addr && typeof addr === "object") {
      const parts = [addr.city_name || addr.cityName, addr.state_name || addr.stateName, addr.country_name || addr.countryName].filter(Boolean);
      if (parts.length) return parts.join(", ");
    }
    return "";
  };

  const getYearsInBusiness = (item) => {
    const fd = item?.facility_details || {};
    const y = fd.yearOfConstruction ?? fd.year_of_construction ?? fd.yearEstablished ?? fd.year_established ?? fd.year_of_establishment;
    const n = parseInt(String(y || "").trim(), 10);
    if (!Number.isFinite(n) || n < 1900 || n > 2100) return "";
    const years = Math.max(0, new Date().getFullYear() - n);
    return years === 0 ? "< 1 Year" : `${years}+ Years`;
  };

  const getTotalOrders = (item) => {
    const fd = item?.facility_details || {};
    const raw =
      item?.totalOrders ??
      item?.total_orders ??
      item?.orderCount ??
      item?.order_count ??
      fd?.totalOrders ??
      fd?.total_orders ??
      fd?.ordersCompleted ??
      fd?.orders_completed;
    const n = toNumber(raw);
    if (!Number.isFinite(n)) return "";
    return `${n.toLocaleString()} Orders`;
  };

  const loadSubmissions = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [submissionsRes, manufacturersRes] = await Promise.allSettled([
        CommonService.get(API_URLS.CONTRACT_MANUFACTURER.MY_RFQ_SUBMISSIONS, { limit: 500, offset: 0 }),
        CommonService.get(API_URLS.CONTRACT_MANUFACTURER.LIST, { page: 1, limit: 500 }),
      ]);
      const payload =
        submissionsRes.status === "fulfilled"
          ? (submissionsRes.value?.data ?? submissionsRes.value ?? {})
          : {};
      const items = Array.isArray(payload?.items) ? payload.items : [];
      const quotedOnly = items.filter(
        (x) =>
          x?.quoteSubmitted ||
          x?.quote_submitted ||
          x?.quote_table_id ||
          x?.quote_status ||
          String(x?.status || "").toLowerCase() === "responded" ||
          String(x?.status || "").toLowerCase() === "quoted"
      );
      const filtered =
        Number.isFinite(companyIdFilter) && companyIdFilter > 0
          ? quotedOnly.filter((x) => Number(x?.company_id) === companyIdFilter)
          : quotedOnly;
      const scoped = scopeRowsToQuoteContext(
        filtered,
        Number.isFinite(rfqIdFilter) && rfqIdFilter > 0 ? rfqIdFilter : null,
      );
      setRows(scoped);

      if (manufacturersRes.status === "fulfilled") {
        const manufacturerPayload = manufacturersRes.value?.data ?? manufacturersRes.value ?? {};
        const manufacturerItems = Array.isArray(manufacturerPayload?.items) ? manufacturerPayload.items : [];
        const nextMeta = new Map();
        manufacturerItems.forEach((m) => {
          const cid = Number(m?.company_id);
          if (!Number.isFinite(cid) || cid <= 0) return;
          nextMeta.set(cid, {
            location: getLocationFromManufacturer(m) || "",
            yearsInBusiness: getYearsInBusiness(m) || "",
            totalOrders: getTotalOrders(m) || "",
          });
        });
        setManufacturerMetaById(nextMeta);
      }
    } catch (e) {
      const m = e?.response?.data?.message || e?.message || "Unable to load quotations";
      setError(m);
      toast.error(m);
    } finally {
      setLoading(false);
    }
  }, [companyIdFilter, rfqIdFilter]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!qcModalOpen || !qcModalReadOnly) return;
      const rfqId = Number(qcModalRfqId);
      if (!Number.isFinite(rfqId) || rfqId <= 0) return;
      try {
        const res = await CommonService.get(API_URLS.CONTRACT_MANUFACTURER.QC_REQUIREMENTS_GET(rfqId));
        const data = res?.data?.data ?? res?.data ?? res;
        const qcRow = data?.qc ?? null;
        const payload = qcRow?.qc_payload_json ?? qcRow?.qcPayloadJson ?? null;
        if (!cancelled) setQcInitialData(payload);
      } catch (e) {
        if (!cancelled) toast.error(extractApiErrorMessage(e));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [qcModalOpen, qcModalReadOnly, qcModalRfqId]);

  useEffect(() => {
    loadSubmissions();
  }, [loadSubmissions]);

  const quoteCards = useMemo(
    () =>
      rows.map((row) => {
        const rfqPayload = getObj(row?.payload_json);
        const quotePayload = getObj(row?.quote_payload_json);
        const project = getObj(rfqPayload?.project);
        const base = getObj(quotePayload?.basePrice);
        const commercial = getObj(quotePayload?.commercial);
        const sample = getObj(quotePayload?.sample);
        const quality = getObj(rfqPayload?.quality);
        const paymentTermsLabel = resolveOptionLabel(
          commercial?.paymentTerms ?? commercial?.payment_terms,
          paymentScheduleOptions
        );
        const paymentMethodLabel = resolveOptionLabel(
          commercial?.paymentMethod ?? commercial?.payment_method,
          paymentMethodOptions
        );
        const incotermsLabel = resolveOptionLabel(
          commercial?.incoterms ?? commercial?.shippingTerms ?? commercial?.shipping_terms,
          shippingTermsOptions
        );
        const transportModeLabel = resolveOptionLabel(
          commercial?.deliveryMethods ?? commercial?.preferredTransport ?? commercial?.transportMode,
          modeOfTransportOptions
        );
        const packaging = getObj(rfqPayload?.packaging);
        const insuranceRaw =
          commercial?.insurance ??
          commercial?.insuranceCoverage ??
          commercial?.insuranceIncluded ??
          packaging?.insurance ??
          packaging?.insuranceCoverage ??
          packaging?.insuranceIncluded;
        const insurance =
          typeof insuranceRaw === "boolean"
            ? insuranceRaw
              ? "Included"
              : "Not Included"
            : String(insuranceRaw ?? "").trim() || "—";
        const amountFallback = row?.submittedQuoteAmount ?? row?.submitted_quote_amount ?? base?.price;
        const totals = computeQuoteTotals(base, amountFallback);
        const currency = row?.quoteCurrency ?? base?.currency ?? "USD";
        const manufacturerMeta = manufacturerMetaById.get(Number(row?.company_id)) || {};
        const buyerSignedAtRaw =
          row?.agreement_buyer_signed_at ?? row?.agreementBuyerSignedAt ?? null;
        const buyerHasSignedAgreement =
          buyerSignedAtRaw != null && String(buyerSignedAtRaw).trim() !== "";
        const mfrSignedAtRaw =
          row?.agreement_mfr_signed_at ?? row?.agreementMfrSignedAt ?? null;
        const manufacturerHasSignedAgreement =
          mfrSignedAtRaw != null && String(mfrSignedAtRaw).trim() !== "";
        const agreementFullySigned = buyerHasSignedAgreement && manufacturerHasSignedAgreement;
        const productCode =
          project?.productCode ||
          project?.product_code ||
          project?.productName ||
          project?.product_name ||
          project?.title ||
          "";
        const internalRfqId =
          resolveInternalCmRfqIdFromPayload(rfqPayload) ??
          (String(row?.flow_type || "").trim() === "p2p_internal" ? Number(row?.id) : null);
        return {
          id: Number(row?.id),
          internalRfqId: Number.isFinite(internalRfqId) && internalRfqId > 0 ? internalRfqId : null,
          quoteTableId:
            row?.quote_table_id != null && Number(row.quote_table_id) > 0
              ? Number(row.quote_table_id)
              : null,
          companyId: Number(row?.company_id),
          companyName: row?.company_name || "Contract Manufacturer",
          rfqNo: `RFQ-${String(row?.id || "").padStart(5, "0")}`,
          quotedAt: row?.quoteSubmittedAt || row?.quote_submitted_at || row?.quote_updated_at || row?.updated_at,
          amount: Number.isFinite(totals.totalAmount) ? totals.totalAmount : amountFallback,
          baseAmount: Number.isFinite(totals.baseAmount) ? totals.baseAmount : amountFallback,
          taxPercent: totals.taxPercent,
          taxAmount: Number.isFinite(totals.taxAmount) ? totals.taxAmount : 0,
          currency,
          deliveryDate: sample?.dispatchLeadTimeDays ? `${sample.dispatchLeadTimeDays} days` : "—",
          paymentTerms: paymentTermsLabel || "—",
          paymentMethod: paymentMethodLabel || "—",
          incoterms: incotermsLabel || "—",
          transportMode: transportModeLabel || "—",
          expectedDispatchDate: commercial?.expectedDispatchDate || "—",
          portOfShipping: commercial?.portOfShipping || "—",
          packagingType: packaging?.packagingSpecification || "—",
          insurance,
          warranty: sample?.warrantyPeriod || sample?.warranty || "—",
          unitPrice: Number.isFinite(totals.baseAmount) ? totals.baseAmount : amountFallback,
          projectTitle: project?.title || "RFQ request",
          productCode: String(productCode || "").trim(),
          quantity: deriveQuantity(rfqPayload, quotePayload, dimensionUnitOptions, attributeUnitOptions),
          status: String(row?.quote_status || "submitted").toLowerCase(),
          acceptanceApprovalStatus:
            row?.acceptance_approval_status ?? row?.acceptanceApprovalStatus ?? "not_required",
          acceptancePendingApproval: !!(
            row?.acceptance_pending_approval ?? row?.acceptancePendingApproval
          ),
          agreementId:
            row?.agreement_id != null && Number(row.agreement_id) > 0 ? Number(row.agreement_id) : null,
          agreementStatus:
            row?.agreement_status != null ? String(row.agreement_status).trim().toLowerCase() : null,
          updatedAt: row?.quote_updated_at || row?.updated_at || row?.created_at,
          commercial,
          sample,
          quality,
          base,
          rfqPayload,
          quotePayload,
          contactEmail: row?.contact_email || "—",
          manufacturerLocation: manufacturerMeta.location || "—",
          manufacturerYearsInBusiness: manufacturerMeta.yearsInBusiness || "—",
          manufacturerTotalOrders: manufacturerMeta.totalOrders || "—",
          buyerSignPending: !!(row?.buyer_sign_pending ?? row?.buyerSignPending),
          buyerHasSignedAgreement,
          agreementFullySigned,
          qcApplied: !!(row?.qc_applied ?? row?.qcApplied),
          sellerQcSubmittedForApproval: !!(row?.sellerQcSubmittedForApproval ?? row?.seller_qc_submitted_for_approval),
          buyerQcNormsApprovedAt: row?.buyer_qc_norms_approved_at ?? row?.buyerQcNormsApprovedAt ?? null,
          buyerExternalEvaluatorDecision:
            row?.buyer_external_evaluator_decision ?? row?.buyerExternalEvaluatorDecision ?? null,
          buyerExternalEvaluatorSelectedQualityInspectorId:
            row?.buyer_external_evaluator_selected_quality_inspector_id ??
            row?.buyerExternalEvaluatorSelectedQualityInspectorId ??
            null,
          qualityApprovalStatus: row?.qualityApprovalStatus ?? row?.quality_approval_status ?? "pending",
          internalInspectionReportSubmitted: !!(
            row?.internalInspectionReportSubmitted ?? row?.internal_inspection_report_submitted
          ),
          qualityApprovalReady: !!(row?.qualityApprovalReady ?? row?.quality_approval_ready),
        };
      }),
    [
      rows,
      dimensionUnitOptions,
      attributeUnitOptions,
      paymentMethodOptions,
      paymentScheduleOptions,
      shippingTermsOptions,
      modeOfTransportOptions,
      manufacturerMetaById,
    ]
  );

  const saveExternalEvaluatorDecision = useCallback(
    async (rfqId, decision) => {
      const id = Number(rfqId);
      if (!Number.isFinite(id) || id <= 0) {
        toast.error("Invalid RFQ id");
        return false;
      }
      try {
        setExternalQaSavingDecision(true);
        await CommonService.post(API_URLS.CONTRACT_MANUFACTURER.QC_EXTERNAL_EVALUATOR_DECISION(id), {
          decision,
        });
        // Refresh list so button/prompt state updates without reload
        await loadSubmissions();
        return true;
      } catch (e) {
        toast.error(extractApiErrorMessage(e));
        return false;
      } finally {
        setExternalQaSavingDecision(false);
      }
    },
    [loadSubmissions]
  );

  const toggleSection = (key) => {
    setExpandedSections((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const stats = useMemo(() => {
    if (quoteCards.length === 0) return { lowest: null, fastest: null, highestRated: null };
    const priced = quoteCards.filter((q) => Number.isFinite(toNumber(q.amount)));
    const lowest = priced.length ? [...priced].sort((a, b) => toNumber(a.amount) - toNumber(b.amount))[0] : null;
    return {
      lowest,
      fastest: quoteCards[0] || null,
      highestRated: quoteCards[0] || null,
    };
  }, [quoteCards]);

  const filteredCards = useMemo(() => {
    if (statusTab === "pending") return quoteCards.filter((q) => q.status === "pending");
    if (statusTab === "accepted") return quoteCards.filter((q) => q.status === "accepted");
    return quoteCards;
  }, [quoteCards, statusTab]);

  const headerCard = filteredCards[0] || quoteCards[0] || null;
  const headerRfqLabel =
    Number.isFinite(rfqIdFilter) && rfqIdFilter > 0
      ? `CM RFQ #${rfqIdFilter}`
      : headerCard?.internalRfqId
        ? `CM RFQ #${headerCard.internalRfqId}`
        : headerCard?.rfqNo || "—";
  const headerLogisticsSourceId =
    Number.isFinite(rfqIdFilter) && rfqIdFilter > 0
      ? String(rfqIdFilter)
      : headerCard?.internalRfqId
        ? String(headerCard.internalRfqId)
        : headerCard?.id
          ? String(headerCard.id)
          : null;

  return (
    <div className="cm-review-page">
      <div className="cm-review-wrap">
        <div className="cm-review-top">
          <div>
            <h1 className="cm-review-title">Review Quotations</h1>
            <p className="cm-review-sub">Compare and select the best quotation for your RFQ</p>
          </div>
        </div>

        {loading && <div className="text-muted">Loading quotations...</div>}
        {!loading && error && <div className="alert alert-warning">{error}</div>}
        {!loading && !error && quoteCards.length === 0 && <div className="alert alert-info">No submitted quotations found.</div>}

        {!loading && !error && headerCard && (
          <>
            <div className="cm-review-rfq-card">
              <div className="cm-review-rfq-head">
                <div><span>RFQ Number</span><strong>{headerRfqLabel}</strong></div>
                <div><span>Product</span><strong>{headerCard.projectTitle}</strong></div>
                <div><span>Quantity</span><strong>{headerCard.quantity}</strong></div>
                <div><span>Quotations Received</span><strong>{quoteCards.length}</strong></div>
                <div><span>Best Price</span><strong>{stats.lowest ? formatCurrency(stats.lowest.amount, stats.lowest.currency) : "—"}</strong></div>
              </div>

              {headerLogisticsSourceId ? (
                <LinkedLogisticsPanel
                  sourceModule="contract_manufacturing"
                  sourceRecordType="manufacturing_rfq"
                  sourceRecordId={headerLogisticsSourceId}
                  linkPurpose="fulfillment"
                  title="CM RFQ logistics"
                  variant="compact"
                  className="cm-review-rfq-logistics"
                />
              ) : null}
            </div>

            <div className="cm-review-stats">
              <div className="cm-review-stat-card">
                <div className="cm-review-stat-icon cm-review-stat-icon--green">
                  <FaDollarSign />
                </div>
                <div className="cm-review-stat-content">
                  <span>Lowest Price</span>
                  <strong>{stats.lowest ? formatCurrency(stats.lowest.amount, stats.lowest.currency) : "—"}</strong>
                  <small>{stats.lowest?.companyName || "—"}</small>
                </div>
              </div>
              <div className="cm-review-stat-card">
                <div className="cm-review-stat-icon cm-review-stat-icon--blue">
                  <FaRegClock />
                </div>
                <div className="cm-review-stat-content">
                  <span>Fastest Delivery</span>
                  <strong>{stats.fastest ? formatDate(stats.fastest.updatedAt) : "—"}</strong>
                  <small>{stats.fastest?.companyName || "—"}</small>
                </div>
              </div>
              <div className="cm-review-stat-card">
                <div className="cm-review-stat-icon cm-review-stat-icon--orange">
                  <FaStar />
                </div>
                <div className="cm-review-stat-content">
                  <span>Highest Rated</span>
                  <strong>4.9</strong>
                  <small>{stats.highestRated?.companyName || "—"}</small>
                </div>
              </div>
            </div>

            <div className="cm-review-tabs">
              <button type="button" className={statusTab === "all" ? "active" : ""} onClick={() => setStatusTab("all")}>
                All Quotations ({quoteCards.length})
              </button>
              <button type="button" className={statusTab === "pending" ? "active" : ""} onClick={() => setStatusTab("pending")}>
                Pending ({quoteCards.filter((q) => q.status === "pending").length})
              </button>
              <button type="button" className={statusTab === "accepted" ? "active" : ""} onClick={() => setStatusTab("accepted")}>
                Accepted ({quoteCards.filter((q) => q.status === "accepted").length})
              </button>
            </div>

            <div className="cm-review-list">
              {filteredCards.map((q) => (
                <div
                  key={`${q.id}-${q.companyId}`}
                  className={`cm-review-quote-card ${
                    stats.lowest && Number(stats.lowest.id) === Number(q.id) && Number(stats.lowest.companyId) === Number(q.companyId)
                      ? "cm-review-quote-card--lowest"
                      : ""
                  }`}
                >
                  <div className="cm-review-quote-head">
                    <div className="cm-review-company">
                      <span className="cm-review-company-icon">
                        <FaFileAlt />
                      </span>
                      <h3>{q.companyName}</h3>
                      <span className="cm-review-verified"><FaCheckCircle /> Verified</span>
                      {stats.lowest && Number(stats.lowest.id) === Number(q.id) && Number(stats.lowest.companyId) === Number(q.companyId) && (
                        <span className="cm-review-lowest-pill">Lowest Price</span>
                      )}
                    </div>
                    <div className="cm-review-quote-meta">
                      <span>QT: {`QT-${String(q.quoteTableId || q.id).padStart(6, "0")}`}</span>
                      <span className="dot">•</span>
                      <span><FaStar className="me-1 text-warning" />4.9</span>
                      <span className="dot">•</span>
                      <span>Submitted: {formatDate(q.quotedAt)}</span>
                    </div>
                  </div>

                  {q.quoteTableId && (
                    <LinkedLogisticsPanel
                      sourceModule="contract_manufacturing"
                      sourceRecordType="manufacturing_quote"
                      sourceRecordId={String(q.quoteTableId)}
                      linkPurpose="fulfillment"
                      title={`Logistics · ${q.companyName}`}
                      extraInitiateParams={{ cmRfqId: q.id }}
                    />
                  )}

                  <div className="cm-review-quote-grid">
                    <div>
                      <label>Total Amount</label>
                      <strong>{formatCurrency(q.amount, q.currency)}</strong>
                      <small>Unit Price: {formatCurrency(q.unitPrice, q.currency)}</small>
                    </div>
                    <div>
                      <label>Delivery</label>
                      <strong>{q.deliveryDate}</strong>
                      <small>Updated: {formatDate(q.updatedAt)}</small>
                    </div>
                    <div>
                      <label>Payment Terms</label>
                      <strong>{q.paymentTerms}</strong>
                    </div>
                    <div>
                      <label>Warranty</label>
                      <strong>{q.warranty}</strong>
                    </div>
                  </div>

                  <div className="cm-review-quote-actions">
                    <div className="left">
                      <CmQuoteIconBtn
                        title="View full quotation details"
                        onClick={() => {
                          setDetailQuote(q);
                          setExpandedSections({
                            commercial: false,
                            delivery: false,
                            quality: false,
                            warranty: false,
                            pricing: false,
                            manufacturer: false,
                          });
                        }}
                      >
                        <FaEye />
                      </CmQuoteIconBtn>
                    </div>
                    <div className="right cm-quote-actions-toolbar">
                      {(() => {
                        const qs = String(q.status || "").toLowerCase();
                        const accepted = qs === "accepted";
                        const rejected = qs === "rejected";
                        const acceptancePending =
                          String(q.acceptanceApprovalStatus || "not_required").toLowerCase() === "pending";
                        const acceptanceRejected =
                          String(q.acceptanceApprovalStatus || "not_required").toLowerCase() === "rejected";
                        const hasAgreementRow = q.agreementId != null && Number(q.agreementId) > 0;
                        const ag = String(q.agreementStatus || "").toLowerCase();

                        if (rejected) {
                          return null;
                        }

                        if (acceptancePending) {
                          return (
                            <span className="cm-review-pending-approval-pill">
                              <FaClock /> Acceptance pending approval
                            </span>
                          );
                        }

                        if (acceptanceRejected) {
                          return (
                            <>
                              <span className="cm-review-pending-approval-pill cm-review-pending-approval-pill--rejected">
                                Acceptance approval rejected
                              </span>
                              <button
                                type="button"
                                className="btn-success"
                                onClick={() => setAcceptQuote(q)}
                              >
                                <FaClock /> Accept & Proceed
                              </button>
                            </>
                          );
                        }

                        if (accepted) {
                          if (!hasAgreementRow) {
                            return (
                              <CmQuoteIconBtn
                                title="Create purchase agreement"
                                primary
                                withLabel
                                onClick={() =>
                                  navigate(`/dashboard/buyer/contract-manufacturers/agreements/create?rfqId=${q.id}`)
                                }
                              >
                                <FaFileAlt />
                                <span>Create</span>
                              </CmQuoteIconBtn>
                            );
                          }
                          if (ag === "draft") {
                            return (
                              <CmQuoteIconBtn
                                title="Complete purchase agreement draft"
                                primary
                                withLabel
                                onClick={() =>
                                  navigate(`/dashboard/buyer/contract-manufacturers/agreements/create?rfqId=${q.id}`)
                                }
                              >
                                <FaFileAlt />
                                <span>Complete</span>
                              </CmQuoteIconBtn>
                            );
                          }
                          if (q.buyerSignPending) {
                            const signHref = `/dashboard/buyer/contract-manufacturers/agreements/sign/${q.id}${
                              Number.isFinite(companyIdFilter) && companyIdFilter > 0
                                ? `?companyId=${companyIdFilter}`
                                : ""
                            }`;
                            return (
                              <>
                                <CmQuoteIconBtn
                                  title="Sign purchase agreement"
                                  primary
                                  withLabel
                                  onClick={() => navigate(signHref)}
                                >
                                  <FaSignature />
                                  <span>Sign</span>
                                </CmQuoteIconBtn>
                                <CmQuoteIconBtn
                                  title="View or edit agreement draft"
                                  withLabel
                                  onClick={() =>
                                    navigate(
                                      `/dashboard/buyer/contract-manufacturers/agreements/create?rfqId=${q.id}`
                                    )
                                  }
                                >
                                  <FaFileAlt />
                                  <span>Agreement</span>
                                </CmQuoteIconBtn>
                              </>
                            );
                          }
                          if (ag === "submitted" || ag === "signed" || ag === "cancelled" || hasAgreementRow) {
                            const signViewHref = `/dashboard/buyer/contract-manufacturers/agreements/sign/${q.id}${
                              Number.isFinite(companyIdFilter) && companyIdFilter > 0
                                ? `?companyId=${companyIdFilter}`
                                : ""
                            }`;
                            const qcTitle = q.qcApplied
                              ? q.sellerQcSubmittedForApproval
                                ? "Review and approve QC norms from manufacturer"
                                : "View QC requirements"
                              : "Set QC requirements";
                            const qaDone =
                              String(q.qualityApprovalStatus || "").toLowerCase() === "approved" ||
                              String(q.qualityApprovalStatus || "").toLowerCase() === "rejected";
                            const qaTitle = qaDone
                              ? "View QA approval decision"
                              : "Approve or reject internal quality inspection report";
                            return (
                              <>
                                {q.buyerHasSignedAgreement ? (
                                  <CmQuoteIconBtn
                                    title={qcTitle}
                                    onClick={() => {
                                      setQcModalProductCode(q.productCode || q.projectTitle || "");
                                      setQcModalRfqId(q.id);
                                      setQcInitialData(null);
                                      setQcModalReadOnly(!!q.qcApplied);
                                      const pendingReview = !!(q.sellerQcSubmittedForApproval && q.qcApplied);
                                      setQcModalBuyerReviewPending(pendingReview);
                                      setQcModalManufacturerName(String(q.companyName || "").trim() || "Manufacturer");
                                      setQcModalOpen(true);
                                    }}
                                  >
                                    <FaClipboardList />
                                  </CmQuoteIconBtn>
                                ) : null}
                                {q.buyerHasSignedAgreement &&
                                q.qcApplied &&
                                (q.qualityApprovalReady || qaDone) ? (
                                  <CmQuoteIconBtn
                                    title={qaTitle}
                                    onClick={() =>
                                      navigate(
                                        `/dashboard/buyer/contract-manufacturers/quality-approval/${q.id}${
                                          Number.isFinite(companyIdFilter) && companyIdFilter > 0
                                            ? `?companyId=${companyIdFilter}`
                                            : ""
                                        }`
                                      )
                                    }
                                  >
                                    <FaShieldAlt />
                                  </CmQuoteIconBtn>
                                ) : null}
                                {q.buyerHasSignedAgreement &&
                                q.qcApplied &&
                                (q.buyerQcNormsApprovedAt != null && String(q.buyerQcNormsApprovedAt).trim() !== "") &&
                                String(q.buyerExternalEvaluatorDecision || "").toLowerCase() !== "no" ? (
                                  <CmQuoteIconBtn
                                    title={
                                      String(q.buyerExternalEvaluatorSelectedQualityInspectorId || "").trim() !== ""
                                        ? "Review or change external quality inspector"
                                        : "Recruit an external quality inspector"
                                    }
                                    disabled={externalQaSavingDecision}
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      const hasInspector =
                                        String(q.buyerExternalEvaluatorSelectedQualityInspectorId || "").trim() !== "";
                                      const loc = buildRecruitExternalQaLocation(q.id, companyIdFilter, q.quoteTableId);
                                      if (!loc) {
                                        toast.error("Invalid RFQ reference. Refresh the page and try again.");
                                        return;
                                      }
                                      if (hasInspector) {
                                        navigate(loc);
                                        return;
                                      }
                                      setExternalQaPromptForRfq(q);
                                    }}
                                  >
                                    <FaUserFriends />
                                  </CmQuoteIconBtn>
                                ) : null}
                                {q.buyerHasSignedAgreement ? (
                                  <CmQuoteIconBtn title="View agreement signatures" onClick={() => navigate(signViewHref)}>
                                    <FaSignature />
                                  </CmQuoteIconBtn>
                                ) : null}
                                {q.agreementFullySigned ? (
                                  <CmQuoteIconBtn
                                    title="Production & logistics (invoice, manifest, GRN)"
                                    primary
                                    withLabel
                                    onClick={() =>
                                      navigate(`/dashboard/buyer/contract-manufacturers/production/${q.id}`)
                                    }
                                  >
                                    <FaTruck />
                                    <span>Production</span>
                                  </CmQuoteIconBtn>
                                ) : null}
                                <CmQuoteIconBtn
                                  title="View purchase agreement"
                                  primary
                                  withLabel
                                  onClick={() =>
                                    navigate(`/dashboard/buyer/contract-manufacturers/agreements/create?rfqId=${q.id}`)
                                  }
                                >
                                  <FaFileAlt />
                                  <span>Agreement</span>
                                </CmQuoteIconBtn>
                              </>
                            );
                          }
                          return null;
                        }

                        return (
                          <>
                            <button
                              type="button"
                              className="btn-outline"
                              onClick={() => {
                                setRejectQuote(q);
                                setRejectReason("");
                                setRejectErrors({});
                              }}
                            >
                              Reject
                            </button>
                            <button type="button" className="btn-success" onClick={() => setAcceptQuote(q)}>
                              <FaClock /> Accept & Proceed
                            </button>
                          </>
                        );
                      })()}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {externalQaPromptForRfq ? (
          <div className="cm-quote-modal-backdrop" onClick={() => setExternalQaPromptForRfq(null)}>
            <div
              className="cm-quote-modal-card cm-quote-modal-card--confirm"
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
              aria-labelledby="cm-extqa-confirm-title"
            >
              <button type="button" className="cm-quote-modal-close" onClick={() => setExternalQaPromptForRfq(null)}>
                <FaTimes />
              </button>
              <h3 id="cm-extqa-confirm-title" className="cm-quote-modal-title cm-extqa-confirm__title">
                Recruit External QA
              </h3>
              <p className="cm-quote-modal-sub cm-extqa-confirm__text">
                Do you want to recruit an external evaluator?
              </p>
              <div className="cm-extqa-confirm__actions">
                <button
                  type="button"
                  className="cm-extqa-confirm__btn cm-extqa-confirm__btn--no"
                  disabled={externalQaSavingDecision}
                  onClick={async () => {
                    const ok = await saveExternalEvaluatorDecision(externalQaPromptForRfq.id, "no");
                    if (ok) setExternalQaPromptForRfq(null);
                  }}
                >
                  No, continue without
                </button>
                <button
                  type="button"
                  className="cm-extqa-confirm__btn cm-extqa-confirm__btn--yes"
                  disabled={externalQaSavingDecision}
                  onClick={async () => {
                    const prompt = externalQaPromptForRfq;
                    if (!prompt) return;
                    const rfqId = prompt.id;
                    const ok = await saveExternalEvaluatorDecision(rfqId, "yes");
                    if (!ok) return;
                    const loc = buildRecruitExternalQaLocation(rfqId, companyIdFilter, prompt.quoteTableId);
                    setExternalQaPromptForRfq(null);
                    if (!loc) {
                      toast.error("Invalid RFQ reference. Refresh the page and try again.");
                      return;
                    }
                    navigate(loc);
                  }}
                >
                  {externalQaSavingDecision ? "Please wait…" : "Yes, choose inspector"}
                </button>
              </div>
            </div>
          </div>
        ) : null}

        {detailQuote && (
          <div className="cm-quote-modal-backdrop" onClick={() => setDetailQuote(null)}>
            <div className="cm-quote-modal-card" onClick={(e) => e.stopPropagation()}>
              <button type="button" className="cm-quote-modal-close" onClick={() => setDetailQuote(null)}>
                <FaTimes />
              </button>
              <h3 className="cm-quote-modal-title">Quotation Details - {detailQuote.companyName}</h3>
              <p className="cm-quote-modal-sub">Complete quotation information for {`QT-${String(detailQuote.id).padStart(6, "0")}`}</p>

              <div className="cm-quote-modal-summary">
                <div><label>Total Amount</label><strong>{formatCurrency(detailQuote.amount, detailQuote.currency)}</strong></div>
                <div><label>Unit Price</label><strong>{formatCurrency(detailQuote.unitPrice, detailQuote.currency)}</strong></div>
                <div><label>Delivery Date</label><strong>{formatDate(detailQuote.updatedAt)}</strong></div>
                <div><label>Lead Time</label><strong>{detailQuote.deliveryDate || "—"}</strong></div>
              </div>

              {[
                { key: "commercial", icon: <FaBalanceScale />, title: "Commercial Terms" },
                { key: "delivery", icon: <FaTruck />, title: "Delivery & Logistics" },
                { key: "quality", icon: <FaShieldAlt />, title: "Quality & Compliance" },
                { key: "warranty", icon: <FaShieldAlt />, title: "Warranty & Support" },
                { key: "pricing", icon: <FaDollarSign />, title: "Pricing Breakdown" },
                { key: "manufacturer", icon: <FaIndustry />, title: "Manufacturer Information" },
              ].map((section) => (
                <div key={section.key} className="cm-quote-modal-section">
                  <button type="button" className="cm-quote-modal-section-head" onClick={() => toggleSection(section.key)}>
                    <span className="left">{section.icon}<span>{section.title}</span></span>
                    <span>{expandedSections[section.key] ? <FaChevronUp /> : <FaChevronDown />}</span>
                  </button>
                  {expandedSections[section.key] && (
                    <div className="cm-quote-modal-section-body">
                      {section.key === "commercial" && (
                        <div className="cm-quote-kv-grid">
                          <div className="cm-quote-kv-item">
                            <label>Payment Terms</label>
                            <strong>{detailQuote.paymentTerms || "—"}</strong>
                          </div>
                          <div className="cm-quote-kv-item">
                            <label>Valid Until</label>
                            <strong>{deriveValidUntil(detailQuote.quotedAt, detailQuote.commercial?.priceValidityDays)}</strong>
                          </div>
                          <div className="cm-quote-kv-item">
                            <label>Pricing Model</label>
                            <strong>{detailQuote.commercial?.preferredPricingModel || "—"}</strong>
                          </div>
                          <div className="cm-quote-kv-item">
                            <label>Incoterms</label>
                            <strong>{detailQuote.incoterms || "—"}</strong>
                          </div>
                          <div className="cm-quote-kv-item">
                            <label>Payment Method</label>
                            <strong>{detailQuote.paymentMethod || "—"}</strong>
                          </div>
                          <div className="cm-quote-kv-item">
                            <label>Currency</label>
                            <strong>{detailQuote.currency || "USD"}</strong>
                          </div>
                        </div>
                      )}
                      {section.key === "delivery" && (
                        <div className="cm-quote-kv-grid cm-quote-kv-grid--two-col">
                          <div className="cm-quote-kv-item">
                            <label>Expected Dispatch Date</label>
                            <strong>{detailQuote.expectedDispatchDate && detailQuote.expectedDispatchDate !== "—" ? formatDate(detailQuote.expectedDispatchDate) : "—"}</strong>
                          </div>
                          <div className="cm-quote-kv-item">
                            <label>Lead Time</label>
                            <strong>{detailQuote.deliveryDate || "—"}</strong>
                          </div>
                          <div className="cm-quote-kv-item">
                            <label>Transport Mode</label>
                            <strong>{detailQuote.transportMode || "—"}</strong>
                          </div>
                          <div className="cm-quote-kv-item">
                            <label>Port of Shipping</label>
                            <strong>{detailQuote.portOfShipping || "—"}</strong>
                          </div>
                          <div className="cm-quote-kv-item">
                            <label>Packaging</label>
                            <strong>{detailQuote.packagingType || "—"}</strong>
                          </div>
                          <div className="cm-quote-kv-item">
                            <label>Insurance</label>
                            <strong>{detailQuote.insurance || "—"}</strong>
                          </div>
                        </div>
                      )}
                      {section.key === "quality" && (
                        <div className="cm-quote-kv-grid cm-quote-kv-grid--three-col">
                          <div className="cm-quote-kv-item">
                            <label>Quality Standards</label>
                            <strong>{detailQuote.quality?.certificateRequired || "—"}</strong>
                          </div>
                          <div className="cm-quote-kv-item">
                            <label>Certificates</label>
                            <strong>{detailQuote.quality?.regulatoryClaims || "—"}</strong>
                          </div>
                          <div className="cm-quote-kv-item">
                            <label>Inspection</label>
                            <strong>{detailQuote.quality?.inspectionRequiredDocument || "—"}</strong>
                          </div>
                          <div className="cm-quote-kv-item">
                            <label>AQL Level</label>
                            <strong>{detailQuote.quality?.acceptableQualityLevel || "—"}</strong>
                          </div>
                          <div className="cm-quote-kv-item">
                            <label>Test Reports</label>
                            <strong>{detailQuote.quality?.testMethodsStandards || "—"}</strong>
                          </div>
                          <div className="cm-quote-kv-item">
                            <label>Compliance</label>
                            <strong>{detailQuote.quality?.regulatoryClaims || "—"}</strong>
                          </div>
                        </div>
                      )}
                      {section.key === "warranty" && (
                        <div className="cm-quote-kv-grid cm-quote-kv-grid--three-col">
                          <div className="cm-quote-kv-item">
                            <label>Warranty Period</label>
                            <strong>{detailQuote.sample?.warrantyPeriod || "—"}</strong>
                          </div>
                          <div className="cm-quote-kv-item">
                            <label>Warranty Coverage</label>
                            <strong>{detailQuote.sample?.warranty || "—"}</strong>
                          </div>
                          <div className="cm-quote-kv-item">
                            <label>Technical Support</label>
                            <strong>{detailQuote.sample?.sampleRemarks || detailQuote.sample?.warrantyRemarks || "—"}</strong>
                          </div>
                          <div className="cm-quote-kv-item">
                            <label>Replacement Policy</label>
                            <strong>{detailQuote.sample?.guarantee || "—"}</strong>
                          </div>
                          <div className="cm-quote-kv-item">
                            <label>Guarantee Period</label>
                            <strong>{detailQuote.sample?.guaranteePeriod || "—"}</strong>
                          </div>
                          <div className="cm-quote-kv-item">
                            <label>Support Remarks</label>
                            <strong>{detailQuote.sample?.guaranteeRemarks || "—"}</strong>
                          </div>
                        </div>
                      )}
                      {section.key === "pricing" && (
                        <div className="cm-quote-pricing-card">
                          <div className="cm-quote-pricing-row">
                            <span>Base Price ({detailQuote.quantity || detailQuote.base?.quantity || detailQuote.base?.moq || "—"})</span>
                            <strong>{formatCurrency(detailQuote.baseAmount, detailQuote.base?.currency || detailQuote.currency)}</strong>
                          </div>
                          <hr />
                          <div className="cm-quote-pricing-row">
                            <span>Tooling/NRE</span>
                            <strong>
                              {detailQuote.commercial?.toolingNre
                                ? formatCurrency(detailQuote.commercial?.toolingNre, detailQuote.base?.currency || detailQuote.currency)
                                : "$0"}
                            </strong>
                          </div>
                          <div className="cm-quote-pricing-row">
                            <span>Taxes ({Number(detailQuote.taxPercent || 0).toFixed(2)}%)</span>
                            <strong>{formatCurrency(detailQuote.taxAmount, detailQuote.currency)}</strong>
                          </div>
                          <div className="cm-quote-pricing-row">
                            <span>Shipping & Handling</span>
                            <strong>Included</strong>
                          </div>
                          <hr />
                          <div className="cm-quote-pricing-row total">
                            <span>Total Amount</span>
                            <strong>{formatCurrency(detailQuote.amount, detailQuote.currency)}</strong>
                          </div>
                        </div>
                      )}
                      {section.key === "manufacturer" && (
                        <div className="cm-quote-kv-grid cm-quote-kv-grid--three-col">
                          <div className="cm-quote-kv-item">
                            <label>Company Name</label>
                            <strong>{detailQuote.companyName || "—"}</strong>
                          </div>
                          <div className="cm-quote-kv-item">
                            <label>Rating</label>
                            <strong><FaStar className="me-1 text-warning" />4.7</strong>
                          </div>
                          <div className="cm-quote-kv-item">
                            <label>Location</label>
                            <strong>{detailQuote.manufacturerLocation || "—"}</strong>
                          </div>
                          <div className="cm-quote-kv-item">
                            <label>Verified Status</label>
                            <strong className="cm-quote-verified-chip"><FaCheckCircle className="me-1" />Verified</strong>
                          </div>
                          <div className="cm-quote-kv-item">
                            <label>Years in Business</label>
                            <strong>{detailQuote.manufacturerYearsInBusiness || "—"}</strong>
                          </div>
                          <div className="cm-quote-kv-item">
                            <label>Total Orders</label>
                            <strong>{detailQuote.manufacturerTotalOrders || "—"}</strong>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {acceptQuote && (
          <div className="cm-accept-modal-backdrop" onClick={() => setAcceptQuote(null)}>
            <div className="cm-accept-modal-card" onClick={(e) => e.stopPropagation()}>
              <button type="button" className="cm-accept-modal-close" onClick={() => setAcceptQuote(null)}>
                <FaTimes />
              </button>
              <h3 className="cm-accept-title">Accept Quotation</h3>
              <p className="cm-accept-sub">
                You are about to accept the quotation from {acceptQuote.companyName}
              </p>

              <div className="cm-accept-summary">
                <h4>Quotation Summary</h4>
                <div className="cm-accept-summary-grid">
                  <div>
                    <label>Quotation Number</label>
                    <strong>{`QT-${String(acceptQuote.id).padStart(6, "0")}`}</strong>
                  </div>
                  <div>
                    <label>Total Amount</label>
                    <strong>{formatCurrency(acceptQuote.amount, acceptQuote.currency)}</strong>
                  </div>
                  <div>
                    <label>Delivery Date</label>
                    <strong>{formatDate(acceptQuote.updatedAt)}</strong>
                  </div>
                  <div>
                    <label>Payment Terms</label>
                    <strong>{acceptQuote.paymentTerms || "—"}</strong>
                  </div>
                </div>
              </div>

              <div className="cm-accept-next">
                <h4>Next Steps</h4>
                <div className="cm-accept-step">
                  <span className="num">1</span>
                  <div>
                    <strong>Create Purchase Agreement</strong>
                    <p>Define contract terms and conditions</p>
                  </div>
                </div>
                <div className="cm-accept-step">
                  <span className="num">2</span>
                  <div>
                    <strong>Digital Signature</strong>
                    <p>Both parties sign the agreement electronically</p>
                  </div>
                </div>
                <div className="cm-accept-step">
                  <span className="num">3</span>
                  <div>
                    <strong>Begin Production</strong>
                    <p>Manufacturer starts production process</p>
                  </div>
                </div>
              </div>

              <div className="cm-accept-actions">
                <button type="button" className="btn-cancel" onClick={() => setAcceptQuote(null)}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn-confirm"
                  onClick={async () => {
                    const q = acceptQuote;
                    try {
                      if (!q?.id) return;
                      const res = await CommonService.post(
                        API_URLS.CONTRACT_MANUFACTURER.ACCEPT_QUOTATION(q.id),
                        {}
                      );
                      const data = res?.data?.data ?? res?.data ?? res;
                      setAcceptQuote(null);
                      if (data?.requires_approval) {
                        toast.success("Quotation acceptance submitted for internal approval");
                        await loadSubmissions();
                        return;
                      }
                      toast.success("Quotation accepted");
                      await loadSubmissions();
                      if (data?.agreement_ready !== false) {
                        navigate(
                          `/dashboard/buyer/contract-manufacturers/agreements/create?rfqId=${q.id}`
                        );
                      }
                    } catch (e) {
                      toast.error(extractApiErrorMessage(e));
                    }
                  }}
                >
                  Accept & Create Agreement
                </button>
              </div>
            </div>
          </div>
        )}

        {rejectQuote && (
          <div className="cm-reject-modal-backdrop" onClick={() => setRejectQuote(null)}>
            <div className="cm-reject-modal-card" onClick={(e) => e.stopPropagation()}>
              <button type="button" className="cm-reject-modal-close" onClick={() => setRejectQuote(null)}>
                <FaTimes />
              </button>
              <h3 className="cm-reject-title">Reject Quotation</h3>
              <p className="cm-reject-sub">Please provide a reason for rejecting this quotation</p>

              <div className="cm-reject-field">
                <label>
                  Reason for Rejection<span className="req">*</span>
                </label>
                <textarea
                  className="cm-reject-textarea"
                  placeholder="Enter reason for rejection..."
                  value={rejectReason}
                  onChange={(e) => {
                    setRejectReason(e.target.value);
                    if (rejectErrors?.reason) setRejectErrors((p) => ({ ...p, reason: undefined }));
                  }}
                />
                {rejectErrors?.reason && <div className="cm-reject-error">{rejectErrors.reason}</div>}
              </div>

              <div className="cm-reject-actions">
                <button type="button" className="btn-cancel" onClick={() => setRejectQuote(null)} disabled={rejecting}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn-reject"
                  disabled={rejecting}
                  onClick={async () => {
                    const q = rejectQuote;
                    const reason = String(rejectReason || "").trim();
                    if (!reason) {
                      setRejectErrors({ reason: "Rejection reason is required" });
                      return;
                    }
                    try {
                      setRejecting(true);
                      await CommonService.post(API_URLS.CONTRACT_MANUFACTURER.REJECT_QUOTATION(q.id), { reason });
                      toast.success("Quotation rejected");
                      setRejectQuote(null);
                      setRejectReason("");
                      setRejectErrors({});
                      await loadSubmissions();
                    } catch (e) {
                      const fe = e?.response?.data?.fieldErrors;
                      if (fe && typeof fe === "object") setRejectErrors(fe);
                      toast.error(extractApiErrorMessage(e));
                    } finally {
                      setRejecting(false);
                    }
                  }}
                >
                  <FaTimesCircle /> Reject Quotation
                </button>
              </div>
            </div>
          </div>
        )}

        <BuyerCmQcRequirementsModal
          open={qcModalOpen}
          productCode={qcModalProductCode}
          initialData={qcInitialData}
          readOnly={qcModalReadOnly}
          buyerReviewPending={qcModalBuyerReviewPending}
          manufacturerName={qcModalManufacturerName}
          onClose={() => {
            setQcModalOpen(false);
            setQcModalProductCode("");
            setQcModalRfqId(null);
            setQcModalReadOnly(false);
            setQcModalBuyerReviewPending(false);
            setQcModalManufacturerName("");
            setQcInitialData(null);
          }}
          onBuyerRejectRequest={async (reason) => {
            const rfqId = Number(qcModalRfqId);
            if (!Number.isFinite(rfqId) || rfqId <= 0) {
              toast.error("Invalid RFQ");
              throw new Error("Invalid RFQ");
            }
            try {
              await CommonService.post(API_URLS.CONTRACT_MANUFACTURER.QC_REQUIREMENTS_REJECT_NORMS(rfqId), {
                reason,
              });
              toast.success("Feedback sent to the manufacturer");
              await loadSubmissions();
            } catch (e) {
              toast.error(extractApiErrorMessage(e));
              throw e;
            }
          }}
          onBuyerApproveRequest={async () => {
            const rfqId = Number(qcModalRfqId);
            if (!Number.isFinite(rfqId) || rfqId <= 0) {
              toast.error("Invalid RFQ");
              throw new Error("Invalid RFQ");
            }
            try {
              await CommonService.post(API_URLS.CONTRACT_MANUFACTURER.QC_REQUIREMENTS_APPROVE_NORMS(rfqId), {});
              toast.success("QC norms approved");
              await loadSubmissions();
            } catch (e) {
              toast.error(extractApiErrorMessage(e));
              throw e;
            }
          }}
          onSave={async (payload) => {
            const rfqId = Number(qcModalRfqId);
            if (!Number.isFinite(rfqId) || rfqId <= 0) {
              toast.error("Invalid RFQ");
              return;
            }
            const action = String(payload?.action || "apply").toLowerCase();
            try {
              if (action === "template") {
                await CommonService.post(API_URLS.CONTRACT_MANUFACTURER.QC_REQUIREMENTS_TEMPLATE, {
                  payload,
                });
                toast.success("Template saved");
              } else {
                await CommonService.post(API_URLS.CONTRACT_MANUFACTURER.QC_REQUIREMENTS_APPLY(rfqId), {
                  payload,
                });
                toast.success("QC requirements applied");
                await loadSubmissions();
                setQcModalReadOnly(true);
              }
              setQcModalOpen(false);
              setQcModalProductCode("");
              setQcModalRfqId(null);
              setQcInitialData(null);
            } catch (e) {
              toast.error(extractApiErrorMessage(e));
            }
          }}
        />
      </div>
    </div>
  );
}

